import firebase from "firebase/app";
import React, { createContext, useContext, useEffect, useState } from "react";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { UserCollection, UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { getUserSubscriptionTier } from "@lifeat/shared/user/UserUtil";
import { Interval } from "@lifeat/shared/util/TypeUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { GA4UserProperties } from "@analytics/GoogleAnalytics/GA4Service";
import { identifyClarity } from "@features/clarity/Clarity";
import { auth } from "@firebase-local/firebase";
import { logError } from "@service/ConsoleLoggerService";
import { useDatabaseContext } from "./DatabaseContext";

export type UserContextType = {
    user: firebase.User | null;
    setUser: (user: firebase.User | null) => void;
    userData: UserEntity | null;
    setUserData: (userData: UserEntity | null) => void;
    loading: boolean;
};

export const UserContext = createContext<UserContextType>({
    user: null,
    setUser: () => {},
    userData: null,
    setUserData: () => {},
    loading: false,
});

interface UserContextProviderProps {
    children?: React.ReactNode;
}

export function UserContextProvider({ children }: UserContextProviderProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const database = useDatabaseContext();
    const [user, setUser] = useState<firebase.User | null>(null);
    const [userData, setUserData] = useState<UserEntity | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [loadingUserData, setLoadingUserData] = useState(true);

    const handleUserDataValue = (userEntity: UserEntity) => {
        if (userEntity && userEntity.email) {
            setUserData(userEntity);

            // For legacy Premium users (no payment was required to get Premium status)
            const legacySubscriptionStatus = userEntity?.isPremiumUser
                ? GA4UserProperties.subscription_status.premium
                : GA4UserProperties.subscription_status.free;
            GA4Service.setUserProperty(GA4UserProperties.subscription_status.key, legacySubscriptionStatus);
            amplitudeService.setUserProp(AmplitudeUserProperties.IS_PREMIUM_USER, userEntity.isPremiumUser);

            // For Pro subscription users
            const subStatus = getUserSubscriptionTier(userEntity);
            amplitudeService.setUserProp(AmplitudeUserProperties.SUBSCRIPTION_TIER, subStatus);
            GA4Service.setUserProperty(GA4UserProperties.subscription_status.key, subStatus);

            // Set personal Room ID as user property
            if (userEntity.roomsData?.personalRoomId) {
                amplitudeService.setUserProp(
                    AmplitudeUserProperties.PERSONAL_ROOM_ID,
                    userEntity.roomsData.personalRoomId,
                );
            }
        }
        setLoadingUserData(false);
    };

    useEffect(() => {
        let refreshTokenInterval: Interval;
        // Auth state change listener
        const unsubscribe = auth.onAuthStateChanged(async function (stateChangeUser: firebase.User | null) {
            let unsubscribeUserData: (() => void) | undefined;

            if (stateChangeUser) {
                setUser(stateChangeUser);

                refreshTokenInterval = setInterval(
                    () => {
                        stateChangeUser.getIdToken(true);
                    },
                    1000 * 60 * 60,
                );

                if (!userData) {
                    const uid = stateChangeUser.uid;
                    setLoadingUserData(true);
                    unsubscribeUserData = database.subscribe(UserCollection.at(uid).node, "value", (result) => {
                        if (!result.success) {
                            logError(`Failed to read data for user UID: ${uid}`);
                        } else if (result.value == null) {
                            logError(`No data available for user UID: ${uid}.`);
                        } else {
                            handleUserDataValue(result.value);
                        }
                    });
                }
            } else {
                setUser(null);
                setUserData(null);
                setLoadingUserData(false);
            }
            setLoadingAuth(false);

            return () => unsubscribeUserData?.();
        });
        // Unsubscribe from the listener when the component is unmounted
        return () => {
            unsubscribe();
            if (refreshTokenInterval) {
                clearInterval(refreshTokenInterval);
            }
        };
    }, []);

    // Init Clarity
    useEffect(() => {
        if (user) {
            identifyClarity(user.uid);
        }
    }, [user]);

    return (
        <UserContext.Provider
            value={{
                user,
                setUser,
                userData,
                setUserData,
                loading: loadingAuth || loadingUserData,
            }}
        >
            {children}
        </UserContext.Provider>
    );
}

export const useUserContext = () => useContext(UserContext);

export default UserContext;
